import { Theme } from '@material-ui/core/styles';

import { utils } from '../../../utils';
import { createWebOrderTheme } from '../../ThemeProvider';
import { breakpoint as breakpointVars } from './config';
import {
  getMuiAccordionSummary,
  getMuiAutocomplete,
  getMuiBackdrop,
  getMuiButton,
  getMuiCheckbox,
  getMuiChip,
  getMuiCssBaseline,
  getMuiDialog,
  getMuiDialogActions,
  getMuiDialogContent,
  getMuiDialogTitle,
  getMuiFormHelperText,
  getMuiIconButton,
  getMuiInput,
  getMuiInputLabel,
  getMuiListItem,
  getMuiMenu,
  getMuiMenuItem,
  getMuiPopover,
  getMuiRadio,
  getMuiSelect,
  getMuiSwitch,
  getMuiTab,
  getMuiTooltip,
  getMuiTypography,
  MuiAccordion,
  MuiAccordionDetails,
  MuiFormControlLabel,
  MuiInputBase,
  MuiList,
  MuiSvgIcon,
  MuiToggleButton,
} from './muiOverrides';
import { getPaletteVars, getTypographyVars } from './tools';

export const createBudapestTheme = (fontName: string): Theme => {
  const paletteVars = getPaletteVars();
  const typeVars = getTypographyVars(fontName);

  const hasColorContrast = utils.check.colorContrast(
    paletteVars.custom.primary.main,
    paletteVars.greyscale[0],
  );
  const primaryContrastText = hasColorContrast
    ? paletteVars.greyscale[0]
    : paletteVars.greyscale[100];

  const themeSettings = {
    breakpoints: {
      values: {
        xs: breakpointVars.values.xs,
        sm: breakpointVars.values.sm,
        md: breakpointVars.values.md,
        lg: breakpointVars.values.lg,
        xl: breakpointVars.values.xl,
      },
    },

    palette: {
      primary: {
        contrastText: primaryContrastText,
        dark: paletteVars.custom.primary.dark,
        light: paletteVars.custom.primary.light,
        main: paletteVars.custom.primary.main,
      },
      common: {
        black: paletteVars.greyscale[1000],
        white: paletteVars.greyscale[0],
      },
      error: {
        contrastText: paletteVars.greyscale[0],
        main: paletteVars.color.error,
      },
      warning: {
        contrastText: paletteVars.greyscale[0],
        main: paletteVars.color.warning,
      },
      success: {
        contrastText: paletteVars.greyscale[0],
        main: paletteVars.color.success,
      },
      info: {
        contrastText: paletteVars.greyscale[0],
        main: paletteVars.color.info,
      },
      grey: {
        200: paletteVars.greyscale[200],
        400: paletteVars.greyscale[400],
        500: paletteVars.greyscale[500],
        600: paletteVars.greyscale[600],
        800: paletteVars.greyscale[800],
      },

      background: {
        default: paletteVars.greyscale[0],
      },
      divider: paletteVars.greyscale[400],
      text: {
        primary: paletteVars.greyscale[1000],
        secondary: paletteVars.greyscale[800],
        disabled: paletteVars.greyscale[500],
      },

      tonalOffset: {
        dark: 0.05,
        light: 0.2,
      },
      transparent: {
        dark: {
          200: paletteVars.transparent.dark[200],
          500: paletteVars.transparent.dark[500],
          800: paletteVars.transparent.dark[800],
          900: paletteVars.transparent.dark[900],
        },
        light: {
          200: paletteVars.transparent.light[200],
          500: paletteVars.transparent.light[500],
          800: paletteVars.transparent.light[800],
          900: paletteVars.transparent.light[900],
        },
      },
    },

    props: {
      MuiInput: {
        disableUnderline: true,
      },
      MuiInputLabel: {
        shrink: true,
      },
    },

    shadows: new Array(25).fill('none') as any,

    shape: {
      borderRadius: 0,
    },

    typography: {
      fontFamily: typeVars.fontFamily,
      fontSize: typeVars.fontSize,
      fontWeightBold: typeVars.fontWeight.bold,
      fontWeightMedium: typeVars.fontWeight.medium,
      fontWeightRegular: typeVars.fontWeight.regular,
      pxToRem: (fontSize) => `${(fontSize / typeVars.fontSize) * 1}rem`,

      h1: {
        fontWeight: typeVars.fontWeight.medium,
        lineHeight: 1.33,
      },
      h2: {
        fontWeight: typeVars.fontWeight.medium,
        lineHeight: 1.33,
      },
      h3: {
        fontWeight: typeVars.fontWeight.medium,
        lineHeight: 1.33,
      },
      body1: {
        fontSize: '1rem',
        fontWeight: typeVars.fontWeight.regular,
        lineHeight: 1.5,
      },
      body2: {
        fontSize: '0.813rem',
        fontWeight: typeVars.fontWeight.regular,
        lineHeight: 1.38,
        letterSpacing: '0.01rem',
      },
      caption: {
        fontSize: '0.75rem',
        fontWeight: typeVars.fontWeight.medium,
        lineHeight: 1.33,
        letterSpacing: '0.01rem',
      },
      button: {
        fontSize: '1.125rem',
        fontWeight: typeVars.fontWeight.bold,
        lineHeight: 1.33,
      },
    },

    zIndex: {
      appBar: 1100,
      drawer: 1200,
      mobileStepper: 1000,
      modal: 999999999,
      snackbar: 1000000000,
      speedDial: 1050,
      tooltip: 1500,
    },
  };

  const theme = createWebOrderTheme(themeSettings);

  theme.overrides = {
    ...theme.overrides,
    MuiAccordion,
    MuiAccordionDetails,
    MuiFormControlLabel,
    MuiInputBase,
    MuiList,
    MuiSvgIcon,
    MuiToggleButton,
    ...getMuiAccordionSummary(theme),
    ...getMuiAutocomplete(theme),
    ...getMuiBackdrop(paletteVars),
    ...getMuiButton(theme),
    ...getMuiCheckbox(theme),
    ...getMuiChip(theme),
    ...getMuiCssBaseline(theme),
    ...getMuiDialog(theme),
    ...getMuiDialogActions(theme),
    ...getMuiDialogContent(theme),
    ...getMuiDialogTitle(theme),
    ...getMuiFormHelperText(theme),
    ...getMuiIconButton(theme),
    ...getMuiInput(theme),
    ...getMuiInputLabel(theme),
    ...getMuiListItem(theme),
    ...getMuiMenu(theme),
    ...getMuiMenuItem(theme),
    ...getMuiPopover(theme),
    ...getMuiRadio(theme),
    ...getMuiSelect(theme),
    ...getMuiSwitch(theme),
    ...getMuiTab(theme),
    ...getMuiTooltip(theme),
    ...getMuiTypography(theme),
  } as any;

  return theme;
};
